// @flow
/** @jsx jsx */

import { Fragment } from 'react';
import { partial, partialRight, uniqBy } from 'lodash';

import { css, jsx } from '@emotion/core';
import {
  type ApiKeys,
  type PaymentSource,
  type PaymentSourceMethod,
  type SupportedPaymentMethod,
  paymentSourceMethods,
} from '@zapier/payment/src/types';
import { Modal, ModalContent, Text } from '@zapier/design-system';
import MessageBar from '@zapier/payment/src/components/MessageBar';
import { type Currency } from '@zapier/payment/src/hooks/useCurrencies';
import { type CurrencyRulesetItem } from '@zapier/payment/src/hooks/useCurrencyRuleset';

import NoPaymentInformation from './NoPaymentInformation';
import PaymentMethodBank from './PaymentMethodBank';
import PaymentMethodCard from './PaymentMethodCard';
import PaymentMethodPaypal from './PaymentMethodPaypal';

export type PaymentMethodsSectionProps = {
  /** API keys for payment processors */
  apiKeys: ApiKeys,
  /** The ruleset of which currencies can be paid using which payment sources */
  currencyRuleset: Array<CurrencyRulesetItem>,
  /** Current payment method */
  currentMethod: ?PaymentSourceMethod,
  /** Edit payment methods in a modal */
  editInModal: boolean,
  /** Error string  */
  error: ?string,
  /** Force user to update card details */
  forceCardUpdate: boolean,
  /** User has a saved payment source */
  hasPaymentSource: boolean,
  /** User is currently editing a payment method */
  isEditing: boolean,
  /** Current payment method editor is loading */
  isEditingInFlight: boolean,
  /** Call when users cancels edit */
  onCancelEdit: () => void,
  /** Call when user edits a value */
  onEdit: PaymentSourceMethod => void,
  /** Call when edit causes an error */
  onErrorEdit: (error: Error, method: PaymentSourceMethod) => void,
  /** Call to hide change success message */
  onHideSuccessMessage: () => void,
  /** Call when to save edit */
  onSaveEdit: ({}, PaymentSourceMethod) => void,
  /** Call to select a payment source */
  onSelectSource: (method: PaymentSourceMethod, hasSwitched: boolean) => void,
  /** Current saved payment method */
  savedSource: ?PaymentSource,
  /** The currently selected currency */
  selectedCurrency: Currency,
  /** Show payment changed successfully message */
  showChangeSuccessMessage: boolean,
  /** User's supported payment methods */
  supportedMethods: Array<SupportedPaymentMethod>,
};

const gridCss = css`
  display: grid;
  grid-gap: 20px;
`;

const resolveSupportedMethods = ({
  currencyRuleset,
  selectedCurrency,
  supportedMethods,
}) => {
  const methodsList = supportedMethods.map(m => m.method);
  const filteredMethods = currencyRuleset
    // filter out any items that the user can't pay with
    .filter(c => methodsList.includes(c.method))
    // filter out any items unrelated to the selected currency
    .filter(c => c.currency === selectedCurrency.name)
    // include `paymentProcessor` in each object
    .map(c => ({
      ...c,
      paymentProcessor: supportedMethods.find(m => m.method === c.method)
        ?.paymentProcessor,
    }));

  return filteredMethods;
};

const renderPaymentMethod = (
  props: PaymentMethodsSectionProps,
  filteredMethods
) => {
  const uniqueMethods = uniqBy(filteredMethods, m => m.method);

  const commonProps = {
    apiKeys: props.apiKeys,
    currentMethod: props.currentMethod,
    // Hide the switch if there's only one possible payment method
    hideSwitch:
      uniqueMethods.length <= 1 &&
      uniqueMethods.every(
        m =>
          m.method === props.savedSource?.method ||
          m.method === props.currentMethod
      ),
    isEditing: !props.editInModal && props.isEditing,
    onCancelEdit: props.onCancelEdit,
    onEdit: partial(props.onEdit, props.currentMethod),
    onErrorEdit: partialRight(props.onErrorEdit, props.currentMethod),
    onSaveEdit: props.onSaveEdit,
    onSelectSource: partialRight(props.onSelectSource, true),
    savedSource:
      props.savedSource && props.savedSource.method === props.currentMethod
        ? props.savedSource
        : null,
    supportedMethods: uniqueMethods,
  };

  switch (props.currentMethod) {
    case paymentSourceMethods.card:
      return (
        <PaymentMethodCard
          {...commonProps}
          error={props.error}
          forceCardUpdate={props.forceCardUpdate}
          isEditingInFlight={props.isEditingInFlight}
        />
      );
    case paymentSourceMethods.paypal:
      return <PaymentMethodPaypal {...commonProps} />;
    case paymentSourceMethods.bank:
      return (
        <PaymentMethodBank
          {...commonProps}
          error={props.error}
          isEditingInFlight={props.isEditingInFlight}
        />
      );
    default:
      // This prevents the AsyncOverlay from blowing up because of null
      // children
      return <div />;
  }
};

const renderPaymentSection = (props: PaymentMethodsSectionProps) => {
  const showPaymentMethod = props.hasPaymentSource || props.isEditing;
  const filteredMethods = resolveSupportedMethods({
    currencyRuleset: props.currencyRuleset,
    selectedCurrency: props.selectedCurrency,
    supportedMethods: props.supportedMethods,
  });

  const changeSuccessMessage = (
    <MessageBar onDismiss={props.onHideSuccessMessage} type="success">
      Changes have been saved
    </MessageBar>
  );

  const hasUnsupportedMethod =
    !filteredMethods.map(m => m.method).includes(props.savedSource?.method) &&
    !props.isEditing;
  const unsupportedMethodMessage = (
    <MessageBar title="Heads up!" type="warning">
      Your payment method doesn't support{' '}
      <strong>
        {props.selectedCurrency.name} {props.selectedCurrency.symbol}
      </strong>
      . Please add a different method before checking out.
    </MessageBar>
  );

  return showPaymentMethod ? (
    <Fragment>
      {props.showChangeSuccessMessage && changeSuccessMessage}
      {hasUnsupportedMethod && unsupportedMethodMessage}
      {renderPaymentMethod(props, filteredMethods)}
    </Fragment>
  ) : (
    <NoPaymentInformation
      currentMethod={props.currentMethod}
      onSelectSource={props.onSelectSource}
      selectedCurrency={props.selectedCurrency}
      supportedMethods={filteredMethods}
    />
  );
};

export const PaymentMethodsSection = (props: PaymentMethodsSectionProps) => {
  return (
    <Fragment>
      {renderPaymentSection(props)}
      {props.isEditing && props.editInModal && (
        <Modal ariaLabel="payment method" onClosed={props.onCancelEdit}>
          <ModalContent>
            <div css={gridCss}>
              <Text type="main-head">Edit Payment Details</Text>
              {renderPaymentSection({ ...props, editInModal: false })}
            </div>
          </ModalContent>
        </Modal>
      )}
    </Fragment>
  );
};
